'use client'

import dynamic from 'next/dynamic'
import { useFilters } from 'plp/hooks/useFilters'
import type { FiltersSearchParams } from 'plp/types/Catalog'
import { EventName } from 'plp/types/eventsObserver'
import { eventsObserver } from 'plp/utils/eventsObserver'
import { type ReactNode, useEffect } from 'react'

import { rangeValueFrom } from '../../../../hooks/useFilters/serializer/serializer'
import type { UseFiltersData } from '../getFiltersData/getFiltersData'

const DynamicSmall = dynamic(
	() => import('./Small/Small').then((mod) => mod.Small),
	{
		loading: () => <div />,
	}
)

const DynamicLarge = dynamic(
	() => import('./Large/Large').then((mod) => mod.Large),
	{
		loading: () => <div />,
	}
)

interface FiltersProps {
	show: boolean
	isSmall: boolean
	hasServerFilters: boolean
	filtersData: UseFiltersData
	defaultFilter?: FiltersSearchParams
	onClose: () => void
}

export function Filters({
	show,
	isSmall,
	hasServerFilters,
	defaultFilter,
	filtersData,
	onClose,
}: FiltersProps): ReactNode | null {
	const { filters, range, catalogItemsData } = filtersData

	const filtersProps = useFilters(
		filters ?? [],
		catalogItemsData ?? [],
		range ?? { max: 0, min: 0 },
		rangeValueFrom(defaultFilter?.range),
		defaultFilter?.order
	)

	const { applySelection } = filtersProps

	const shouldRender = show && filters && range

	useEffect(() => {
		const applyChanges = () => {
			applySelection()
		}
		eventsObserver.subscribe(EventName.APPLY_FILTERS, applyChanges)
		return () => {
			eventsObserver.unsubscribe(applyChanges)
		}
	}, [applySelection])

	useEffect(() => {
		if (hasServerFilters) {
			applySelection()
		}
	}, [])

	return (
		shouldRender &&
		(isSmall ? (
			<DynamicSmall
				onClose={onClose}
				filters={filters}
				filtersProps={filtersProps}
			/>
		) : (
			<DynamicLarge
				onClose={onClose}
				onApply={onClose}
				filters={filters}
				filtersProps={filtersProps}
			/>
		))
	)
}
